/**
 * /reset-password?token=..., set a new password using a token from email.
 *
 * Inside AuthShell. New + confirm password with inline validation on blur.
 * On success redirects to /sign-in with ?reset=success so SignIn could show
 * a toast (future enhancement).
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_POLICY_HINT,
  validatePassword,
} from '@olytic/shared';

import { resetPassword, ApiError } from '../lib/api.ts';
import { AuthShell } from '../components/AuthShell.tsx';
import { PasswordInput } from '../components/PasswordInput.tsx';
import { useToast } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  // Sub-agent AUTONOMOUS-CLOSE (2026-08-02) — Fix A. broadcastSignOut fires
  // on same-browser other tabs so if the user has a stale authenticated
  // session in another tab (say they had the app open in one tab, opened
  // the reset link in another), it flips to unauthenticated immediately.
  // The server-side revoke handles cross-device sessions; this handles
  // the same-browser echo.
  const { broadcastSignOut } = useSession();
  const token = searchParams.get('token') ?? '';
  // Push #88 (preempt G): forgot-password endpoint encodes email in the
  // reset link's redirectTo, so after a successful reset we can land on
  // /sign-in?email=... with the field pre-filled. Empty when missing
  // (back-compat for older reset links).
  const emailHint = searchParams.get('email') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [passwordBlurred, setPasswordBlurred] = useState(false);
  const [confirmBlurred, setConfirmBlurred] = useState(false);

  const passwordMismatch = confirmBlurred && confirmPassword !== '' && newPassword !== confirmPassword;
  // Sub-agent PRE-LAUNCH-FIX FIX 4 — shared password policy.
  const passwordPolicyResult =
    passwordBlurred && newPassword !== '' ? validatePassword(newPassword) : null;
  const passwordPolicyMsg =
    passwordPolicyResult !== null && !passwordPolicyResult.ok
      ? (passwordPolicyResult.error ?? null)
      : null;

  if (token === '') {
    return (
      <AuthShell
        title="This link is missing its token"
        subtitle="Reset links from email contain a security token. If you got here from your email and still see this, the link may have been mangled by your email client. Request a fresh one."
        footer={
          <Link to="/sign-in" style={{ color: 'var(--olytic-obsidian)', fontWeight: 500 }}>
            Back to sign in
          </Link>
        }
      >
        <Link to="/forgot-password" className="btn btn-primary btn-block">
          Request a new reset link
        </Link>
      </AuthShell>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const pwCheck = validatePassword(newPassword);
    if (!pwCheck.ok) {
      setError(pwCheck.error ?? 'Password does not meet the policy.');
      setPasswordBlurred(true);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      setConfirmBlurred(true);
      return;
    }

    setSubmitting(true);
    try {
      // Sub-agent AUTONOMOUS-CLOSE (2026-08-02) — Fix A. Pass emailHint
      // (from the reset URL) so the server can look up the userId and
      // revoke every existing session across all devices. Degrades to
      // TTL-only when emailHint is empty (link came without the query
      // param). See apps/ui/functions/api/reset-password.ts for the
      // full session-revoke path.
      await resetPassword(token, newPassword, emailHint === '' ? undefined : emailHint);
      // Same-browser other-tab echo: signal any authenticated tab to
      // flip to unauthenticated. Server-side revoke already killed the
      // sessions upstream; this is the immediate UI signal in-browser.
      broadcastSignOut();
      showToast({ kind: 'success', message: 'Password updated. Sign in with the new one.' });
      // Push #88 (preempt G): pre-fill /sign-in?email=... so the user
      // doesn't have to retype the address they just reset.
      const signInPath =
        emailHint !== ''
          ? `/sign-in?email=${encodeURIComponent(emailHint)}`
          : '/sign-in';
      navigate(signInPath, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reset failed. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Choose a new password"
      subtitle={PASSWORD_POLICY_HINT}
    >
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-row">
          <label className="form-label" htmlFor="newPassword">New password</label>
          <div onBlur={() => setPasswordBlurred(true)}>
            <PasswordInput
              id="newPassword"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              required
              disabled={submitting}
              autoFocus
            />
          </div>
          <div
            className="form-hint"
            style={{ color: passwordPolicyMsg !== null ? 'var(--olytic-red)' : undefined }}
          >
            {passwordPolicyMsg ?? PASSWORD_POLICY_HINT}
          </div>
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="confirmPassword">Confirm new password</label>
          <div onBlur={() => setConfirmBlurred(true)}>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              required
              disabled={submitting}
            />
          </div>
          {passwordMismatch && (
            <div className="form-hint" style={{ color: 'var(--olytic-red)' }}>
              Passwords don't match.
            </div>
          )}
        </div>

        {error !== null && (
          <div className="callout callout-danger">
            <p className="callout-body">{error}</p>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={submitting || passwordMismatch || passwordPolicyMsg !== null}
        >
          {submitting ? (
            <span className="loading-inline">
              <span className="spinner" /> Saving…
            </span>
          ) : (
            'Save new password'
          )}
        </button>
      </form>
    </AuthShell>
  );
}
